/* eslint-disable camelcase */
import React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { createSelector, createStructuredSelector } from 'reselect';
import store from 'store/Store';
import watch from 'redux-watch';

// Selectors
import { superChatSelector } from 'ai/selectors/superChatSelector';
import { pageSelector } from 'selectors/page/pageSelector';
import { coreUserDataSelector } from 'viewModel/selectors/user/coreUserDataSelector';
import modalsSelector from 'selectors/modals/modalsSelector';

// Services
import sdnApi from 'ai/services/sdn';

// Actions
import SuperChatActions from 'ai/actions/superChatActions';

// Utils
import FrameworkUtils from 'utils/framework';
import LanguageLocaleUtils from 'utils/LanguageLocale';
import { getSuperChatConfig, setSuperChatConfig } from 'ai/utils/sessionStorage';
import { getAnonymousId } from 'ai/utils/aiBeautyChat';

// Constants
import { INTENTIONS, ENTRYPOINTS } from 'ai/constants/superchat';
import { CATEGORIES } from 'ai/constants/categories';

const { wrapHOC, wrapHOCComponent } = FrameworkUtils;
const { getTextFromResource, getLocaleResourceFile, getCurrentLanguage, getCurrentCountry } = LanguageLocaleUtils;
const getText = getLocaleResourceFile('ai/components/SuperChat/locales', 'SuperChat');
const country = getCurrentCountry();
const language = getCurrentLanguage();

const { managerSession, superChatConfig, previousConversations } = sdnApi;

const localization = createStructuredSelector({
    title: getTextFromResource(getText, 'title'),
    placeholder: getTextFromResource(getText, 'placeholder'),
    errorMessage: getTextFromResource(getText, 'errorMessage'),
    resendYourMessage: getTextFromResource(getText, 'resendYourMessage'),
    lookingForAGift: getTextFromResource(getText, 'lookingForAGift')
});

const functions = (dispatch, _ownProps) => ({
    createSession: async sessionFound => {
        return managerSession(sessionFound).then(data => data);
    },
    closeSuperChat: () => {
        dispatch(SuperChatActions.showSuperChat(false));
    },
    fetchPreviousConversations: (clientId, anonymousId) => {
        return previousConversations(clientId, anonymousId)
            .then(data => data)
            .catch(_ => []);
    },
    storeSuperChatConfig: config => {
        dispatch(SuperChatActions.superChatConfig(config));
    },
    openSuperChatFromGiftFinder: (prompt, defaultPrompt) => {
        const data = {
            entrypoint: ENTRYPOINTS.PLP,
            category_id: CATEGORIES.GIFT,
            selectedPrompt: prompt || defaultPrompt
        };

        dispatch(SuperChatActions.showSuperChat(true, [], data));
    }
});

const fields = createSelector(
    superChatSelector,
    coreUserDataSelector,
    localization,
    pageSelector,
    modalsSelector,
    (superChat, user, locales, page, modals) => {
        const clientId = user?.biId;
        const isSignInModalOpen = modals?.showSignInModal || modals?.showSignInWithMessagingModal || modals?.showAuthenticateModal;

        return {
            localization: locales,
            clientId,
            isSignInModalOpen,
            productBrandName: page?.product?.productDetails?.brand?.displayName,
            productName: page?.product?.productDetails?.displayName,
            nthCategoryName: page?.nthCategory?.displayName,
            ...superChat
        };
    }
);

const withSuperChatProps = compose(
    wrapHOC(connect(fields, functions)),
    wrapHOC(WrappedComponent => {
        class SuperChatProps extends React.Component {
            state = {
                config: null,
                anonymousId: null
            };

            componentDidMount() {
                const watchLocation = watch(store.getState, 'historyLocation');
                const { clientId, openSuperChatFromGiftFinder } = this.props;
                const superChatConfigCached = getSuperChatConfig();
                const anonymousId = getAnonymousId();

                if (Sephora.configurationSettings.genAIGiftFinderV2?.isEnabled) {
                    store.subscribe(
                        watchLocation(location => {
                            if (location?.queryParams?.intention?.[0] === INTENTIONS.GIFT_FINDER) {
                                setTimeout(() => {
                                    openSuperChatFromGiftFinder(location?.queryParams?.prompt?.[0], this.props.localization?.lookingForAGift);
                                }, 1000); // Delay to ensure the page has loaded
                            }
                        })
                    );
                }

                if (superChatConfigCached) {
                    this.setState({ config: superChatConfigCached, anonymousId });
                    this.props.storeSuperChatConfig(superChatConfigCached);

                    return;
                }

                superChatConfig(language, country, clientId, anonymousId).then(data => {
                    setSuperChatConfig(data?.data?.config);
                    this.props.storeSuperChatConfig(data?.data?.config);
                    this.setState({ config: data?.data?.config, anonymousId });
                });
            }

            render() {
                const { config, anonymousId } = this.state;

                return (
                    <WrappedComponent
                        {...this.props}
                        config={config}
                        language={language}
                        country={country}
                        anonymousId={anonymousId}
                    />
                );
            }
        }

        return wrapHOCComponent(SuperChatProps, 'SuperChatProps', [WrappedComponent]);
    })
);

export {
    withSuperChatProps, fields, functions
};
